import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Modal, Image, Group, UnstyledButton, Text } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useAtom, useSetAtom } from 'jotai'
import { activeChatCharacterAtom } from '../../atoms/chatAtoms'
import { activeStoryThemeAtom } from '../../atoms/themeAtoms'
import { parseCharacterStream } from '../../lib/responseParser'
import { useSpeech } from '../../hooks/useSpeech'
import { activeSpeechStoryIdAtom } from '../../atoms/speechAtoms'
import { buildBeatSpeechQueue } from '../../lib/speechSegments'
import { ThreadTitleCard } from '../game/ThreadTitleCard'
import { ChatPanel } from '../game/ChatPanel'
import { DocumentsPanel } from '../game/DocumentsPanel'
import { BeatTimeline } from '../game/BeatTimeline'
import { SettingsPanel } from '../common/SettingsPanel'
import { MissionBar } from './MissionBar'
import { RecallBanner } from './RecallBanner'
import { StoryStream } from './StoryStream'
import { ResponseDock } from './ResponseDock'
import { StatusStrip } from './StatusStrip'
import { SceneWindow } from './SceneWindow'
import { CastDock } from './CastDock'
import { UtilityTray } from './UtilityTray'
import './game2.css'

// GameScreen2 — the redesigned story player. Two full-height regions: a Story
// Column (2/3) holding Recall Banner, Story Stream, Response Dock; and a Context
// Rail (1/3) holding Status Strip, Scene Window, Cast Dock, Utility Tray. The
// rail takes over for chat or docs. On mobile the rail collapses to a drawer.
//
// Pure presentation: reuses src/lib/player state via the same props GameScreen
// takes; never imports or mutates src/lib/player.
export function GameScreen2({
  story,
  sessionRole,
  isTeamPlay,
  teamName,
  playerRoles,
  serverSessionId,
  currentBeat,
  currentThread,
  allThreads,
  currentThreadIndex,
  progress,
  overallProgress,
  selectedChoice,
  inputValue,
  generatedContent,
  isLoading,
  beatsLoading,
  canGoNext,
  onNext,
  onChoiceSelect,
  onInputSubmit,
  onInputChange,
  canGoPrev,
  onPrev,
  showTitleCard,
  onTitleCardNext,
  isCurrentBeatLocked,
  committedChoices,
  beats,
  currentIndex,
  liveThreadIndex,
  liveBeatIndex,
  isPositionReachable,
  jumpToPosition,
  skipToCurrent,
  reviewMode = false,
}) {
  const navigate = useNavigate()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [railOpen, { open: openRail, close: closeRail }] = useDisclosure(false)
  const [sceneOverlay, { open: openScene, close: closeScene }] = useDisclosure(false)
  const [mapOpen, { open: openMap, close: closeMap }] = useDisclosure(false)
  const [settingsOpen, { open: openSettings, close: closeSettings }] = useDisclosure(false)
  const [docsOpen, setDocsOpen] = useState(false)
  const [activeChatId, setActiveChatId] = useAtom(activeChatCharacterAtom)

  // Apply the story's theme while playing; clear on exit (same as GameScreen).
  const setActiveStoryTheme = useSetAtom(activeStoryThemeAtom)
  const storyThemeId = story?.options?.theme || null
  useEffect(() => {
    setActiveStoryTheme(storyThemeId)
    return () => setActiveStoryTheme(null)
  }, [storyThemeId, setActiveStoryTheme])

  // Close any rail takeover when the rail itself closes on mobile.
  useEffect(() => {
    if (!railOpen && isMobile) {
      setActiveChatId(null)
      setDocsOpen(false)
    }
  }, [railOpen, isMobile, setActiveChatId])

  const characters = story?.characters || []
  const chatCharacter = activeChatId ? characters.find((c) => c.id === activeChatId) : null

  // Speech: per-segment play buttons (always available) + "speak everything"
  // auto-play (governed by the resolved per-story / global setting). speakContext
  // is threaded into the story stream so each segment gets a play control. Only
  // meaningful on this (new) interface — the backend gates the endpoints too.
  const { autoPlay, playQueue, stop: stopSpeech } = useSpeech()
  const speakContext = serverSessionId ? { sessionId: serverSessionId, beatId: currentBeat?.id } : null

  // Tell the speech atoms which story is active so the per-story auto-play
  // override resolves correctly. Clear on unmount.
  const setActiveSpeechStoryId = useSetAtom(activeSpeechStoryIdAtom)
  useEffect(() => {
    setActiveSpeechStoryId(story?.id || null)
    return () => setActiveSpeechStoryId(null)
  }, [story?.id, setActiveSpeechStoryId])

  // Stop audio when actually LEAVING a beat (a real id change), then auto-play
  // the new one if enabled. Both are done in one effect, in order, so the
  // "stop on navigation" never races with — and cancels — the auto-play that's
  // starting for the newly-arrived beat. (playQueue also calls stop() itself, so
  // switching beats supersedes prior audio cleanly.)
  const prevBeatIdRef = useRef(null)
  const autoPlayKey = currentBeat?.id && !generatedContent?.streaming && !generatedContent?.loading
    ? `${currentBeat.id}:${generatedContent?.data ? 'ready' : 'plain'}`
    : null
  useEffect(() => {
    const beatChanged = prevBeatIdRef.current !== currentBeat?.id
    if (beatChanged && prevBeatIdRef.current !== null) stopSpeech()
    prevBeatIdRef.current = currentBeat?.id

    if (!autoPlay || !serverSessionId || !currentBeat) return
    const queue = buildBeatSpeechQueue({ sessionId: serverSessionId, beat: currentBeat, generatedContent })
    if (queue.length) playQueue(queue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayKey, autoPlay, serverSessionId])

  // Beats have no single speaker — on a character_response beat, multiple
  // characters can reply. Highlight whoever actually responded this beat (parsed
  // from the generated content); no highlight on narrative/decision/input beats.
  const respondingCharacterIds = useMemo(() => {
    if (currentBeat?.beat_type !== 'character_response') return []
    const data = generatedContent?.data
    const entries = typeof data === 'string'
      ? parseCharacterStream(data)
      : Array.isArray(data) ? data : []
    const ids = new Set()
    for (const e of entries) {
      const match = characters.find((c) => c.id === e.character_id || c.id === e.character_name)
      if (match) ids.add(match.id)
    }
    return [...ids]
  }, [currentBeat?.beat_type, generatedContent, characters])

  const roleName = sessionRole
    ? ((playerRoles || []).find((r) => r.key === sessionRole)?.name || sessionRole)
    : null

  const sceneUrl =
    currentBeat?.background_url ||
    currentThread?.background_url ||
    story?.default_background_url ||
    null

  const hasDocuments = (story?.player_documents_count || 0) > 0

  // Recall: paraphrase of the most recent committed decision before the current
  // beat. Walk backwards through the loaded beats for the last decision with a
  // committed choice.
  const recallText = useMemo(() => {
    if (!Array.isArray(beats) || !committedChoices) return null
    for (let i = Math.min(currentIndex - 1, beats.length - 1); i >= 0; i--) {
      const b = beats[i]
      if (b?.beat_type === 'decision' && committedChoices[b.id]?.text) {
        return committedChoices[b.id].text
      }
    }
    return null
  }, [beats, committedChoices, currentIndex])

  const threadProgress = typeof progress === 'number' ? progress : undefined

  const closeRailTakeovers = () => {
    setActiveChatId(null)
    setDocsOpen(false)
  }

  const railIsTakenOver = !!chatCharacter || docsOpen

  // Context Rail content: either a takeover (chat / docs) or the default stack.
  const railBody = railIsTakenOver ? (
    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {chatCharacter ? (
        <ChatPanel
          character={chatCharacter}
          storyId={story?.id}
          serverSessionId={serverSessionId}
          expanded={false}
          onToggleExpand={closeRailTakeovers}
        />
      ) : (
        <>
          {/* DocumentsPanel has no close affordance of its own, so the docs
              takeover gets a header to return to the default rail (Status/Scene/
              Cast). Chat provides its own via ChatPanel's collapse control. */}
          <Group
            justify="flex-start"
            px="md"
            py={8}
            style={{ borderBottom: '1px solid var(--bw-panel-border)', flexShrink: 0 }}
          >
            <UnstyledButton onClick={closeRailTakeovers} aria-label="Back to story">
              <Group gap={6} wrap="nowrap">
                <IconArrowLeft size={16} style={{ color: 'var(--bw-accent)' }} />
                <Text size="sm" fw={600} ff="var(--bw-heading-font)" style={{ color: 'var(--bw-accent)' }}>
                  Back to story
                </Text>
              </Group>
            </UnstyledButton>
          </Group>
          <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <DocumentsPanel
              storyId={story?.id}
              sessionRole={sessionRole}
              rolesRequired={(playerRoles?.length || 0) > 0}
              hasSession={!!serverSessionId}
            />
          </Box>
        </>
      )}
    </Box>
  ) : (
    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      <StatusStrip overallProgress={overallProgress} threadProgress={threadProgress} />
      <SceneWindow sceneUrl={sceneUrl} onEnlarge={openScene} characters={characters} />
      <CastDock characters={characters} activeCharacterIds={respondingCharacterIds} />
      <UtilityTray
        hasDocuments={hasDocuments}
        onOpenDocs={() => { setDocsOpen(true); if (isMobile) openRail() }}
        onOpenSettings={openSettings}
      />
    </Box>
  )

  return (
    <Box className="g2-root">
      <MissionBar
        storyName={story?.name}
        threadIndex={currentThreadIndex}
        threadCount={allThreads?.length}
        threadTitle={currentThread?.name}
        isTeamPlay={isTeamPlay}
        teamName={teamName}
        roleName={roleName}
        reviewMode={reviewMode}
        onStoryMap={openMap}
        onExit={reviewMode ? () => navigate('/profile') : undefined}
        onToggleRail={openRail}
        showRailToggle={isMobile}
      />

      <Box className="g2-grid">
        {/* Story Column */}
        <Box className="g2-story-column">
          {showTitleCard ? (
            <ThreadTitleCard
              thread={currentThread}
              threadIndex={currentThreadIndex}
              threadCount={allThreads?.length || 1}
              onNext={onTitleCardNext}
            />
          ) : (
            <>
              <RecallBanner recallText={recallText} onReview={openMap} />
              <StoryStream
                currentBeat={currentBeat}
                characters={characters}
                generatedContent={generatedContent}
                speakContext={speakContext}
              />
              <ResponseDock
                currentBeat={currentBeat}
                selectedChoice={selectedChoice}
                inputValue={inputValue}
                isLoading={isLoading || beatsLoading}
                canGoNext={canGoNext}
                onNext={onNext}
                onChoiceSelect={onChoiceSelect}
                onInputSubmit={onInputSubmit}
                onInputChange={onInputChange}
                canGoPrev={canGoPrev}
                onPrev={onPrev}
                locked={isCurrentBeatLocked || reviewMode}
              />
            </>
          )}
        </Box>

        {/* Context Rail (drawer on mobile) */}
        {isMobile && railOpen && <Box className="g2-scrim" onClick={closeRail} />}
        <Box className={`g2-context-rail${isMobile && railOpen ? ' is-open' : ''}`}>
          {railBody}
        </Box>
      </Box>

      {/* Scene enlarge overlay — full screen so the art uses as much of the
          viewport as possible; the image scales to fit within it. */}
      <Modal
        opened={sceneOverlay}
        onClose={closeScene}
        fullScreen
        withCloseButton
        padding={0}
        styles={{
          body: { height: '100%', padding: 0 },
          content: { display: 'flex', flexDirection: 'column' },
        }}
      >
        {sceneUrl && (
          <Box style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
            <Image
              src={sceneUrl}
              alt="Scene art"
              fit="contain"
              style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto' }}
            />
          </Box>
        )}
      </Modal>

      {/* Settings overlay — opens in-place so the player (and its Recall
          section, story text, progress) stays mounted underneath instead of
          navigating away to the standalone /settings route. */}
      <Modal opened={settingsOpen} onClose={closeSettings} size="md" title="Settings" centered>
        <SettingsPanel />
      </Modal>

      {/* Story map / review timeline overlay */}
      <Modal opened={mapOpen} onClose={closeMap} size="lg" title="Story map" centered>
        <BeatTimeline
          threads={allThreads}
          beats={beats}
          currentThreadIndex={currentThreadIndex}
          currentIndex={currentIndex}
          liveThreadIndex={liveThreadIndex}
          liveBeatIndex={liveBeatIndex}
          isReachable={isPositionReachable}
          onJump={(t, b) => { jumpToPosition?.(t, b); closeMap() }}
          onSkipToCurrent={() => { skipToCurrent?.(); closeMap() }}
        />
      </Modal>
    </Box>
  )
}
